import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TextNode } from 'lexical';
import { useEffect } from 'react';

import { isKnownVariable } from '@/modules/presentation/clientPresentation/presentationString/usePresentationStringFormatter.formatters';

import {
  $createPresentationVariableNode,
  PresentationVariableNode,
} from './PresentationVariableNode';

const VARIABLE_PATTERN = /\{([^{}]+)\}/g;

interface PresentationVariablePluginProps {
  /** When provided, resolved variables display their value (without braces)
   *  instead of the raw `{Variable name}` token. Both resolved and unresolved
   *  variables receive the colored background. */
  resolveVariable?: (variableName: string) => string | null;
}

/**
 * Plugin that detects `{Variable name}` patterns in text nodes and replaces
 * them with PresentationVariableNode instances, which render with a colored
 * background. Only intended for read-only (display) mode — the styled nodes
 * are never persisted because the read-only editor is ephemeral.
 */
export function PresentationVariablePlugin({
  resolveVariable,
}: PresentationVariablePluginProps = {}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([PresentationVariableNode])) {
      throw new Error(
        'PresentationVariablePlugin: PresentationVariableNode not registered on editor'
      );
    }

    return editor.registerNodeTransform(TextNode, (node) => {
      // Don't transform PresentationVariableNode instances (they extend
      // TextNode, but Lexical dispatches transforms by registered type, so
      // this guard handles edge cases).
      if (node instanceof PresentationVariableNode) return;

      const text = node.getTextContent();
      if (!VARIABLE_PATTERN.test(text)) return;

      // Reset lastIndex after the test() call above
      VARIABLE_PATTERN.lastIndex = 0;

      const segments: { text: string; isVariable: boolean }[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = VARIABLE_PATTERN.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.slice(lastIndex, match.index),
            isVariable: false,
          });
        }

        // Attempt to resolve the variable to its display value.
        // variableName is the text inside the braces (e.g. "Current date").
        const variableName = match[1]!; // safe: regex capture group always present

        if (!isKnownVariable(variableName)) {
          segments.push({ text: match[0], isVariable: false });
        } else {
          const resolved = resolveVariable?.(variableName) ?? null;
          const displayText = resolved ?? match[0];
          segments.push({ text: displayText, isVariable: true });
        }
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        segments.push({ text: text.slice(lastIndex), isVariable: false });
      }

      // If no segment is actually a known variable, leave the node as-is.
      // Replacing it with new TextNodes containing the same `{…}` text would
      // re-trigger this transform and cause an infinite loop.
      if (!segments.some((s) => s.isVariable)) return;

      // Build replacement nodes
      const nodes = segments.map((segment) =>
        segment.isVariable
          ? $createPresentationVariableNode(segment.text)
          : new TextNode(segment.text)
      );

      // Replace the original node with the split segments.
      // Insert all new nodes after the original, then remove the original.
      const firstNode = nodes[0];
      if (!firstNode) return;

      node.replace(firstNode);
      let prev = firstNode;
      for (let i = 1; i < nodes.length; i++) {
        const current = nodes[i]!; // safe: i < nodes.length
        prev.insertAfter(current);
        prev = current;
      }
    });
  }, [editor, resolveVariable]);

  return null;
}
